import React, { useEffect, useMemo, useState } from "react";
import {
    Autocomplete, Avatar, Box, Button, Checkbox, Chip, CircularProgress,
    FormControlLabel, Grid, IconButton, ListItemButton, ListItemText,
    Paper, Switch, TextField, Tooltip, Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import PersonIcon from "@mui/icons-material/Person";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import CreateDealerDialog from "../../components/CreateDealerDialog";
import {
    clearCreateFarmerCommitmentAction,
    clearfarmerCommitmentAction,
    createFarmerCommitmentAction,
    farmerCommitmentAction,
    getAllCropsAction,
    getAllDealersAction,
    getAllDistrictsAction,
    getAllFarmerCommitmentsAction,
    getAllMandalsAction,
    getAllSeasonsAction,
    getAllVarietiesAction,
    getAllVillagesAction,
} from "../../store/actions";

/* ── Search-or-Create Location Input ── */
const LocationSearchInput = ({ label, value, selectedId, suggestions, onTextChange, onSelect, disabled, error, helperText }) => {
    const [focused, setFocused] = useState(false);

    const filtered = useMemo(() => {
        if (!value?.trim()) return [];
        return suggestions.filter(s => s.label.toLowerCase().includes(value.toLowerCase().trim()));
    }, [value, suggestions]);

    const showSuggestions = focused && value?.trim().length > 0 && filtered.length > 0;
    const isNew = value?.trim().length > 0 && !selectedId;

    let displayHelper = "";
    let helperColor;
    if (error && helperText) {
        displayHelper = helperText;
        helperColor = "error.main";
    } else if (selectedId) {
        displayHelper = `✓ Existing ${label.toLowerCase()} selected`;
        helperColor = "success.main";
    } else if (isNew) {
        displayHelper = `New ${label.toLowerCase()} will be created`;
        helperColor = "info.main";
    }

    return (
        <Box sx={{ position: "relative" }}>
            <TextField
                fullWidth size="small" label={label} value={value}
                onChange={e => onTextChange(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 200)}
                disabled={disabled} error={error} helperText={displayHelper}
                placeholder={`Search or create ${label.toLowerCase()}`}
                FormHelperTextProps={{ sx: { color: helperColor } }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: disabled ? "#F3F4F6" : "#FAFAFA" } }}
            />
            {showSuggestions && (
                <Paper elevation={4} sx={{ position: "absolute", zIndex: 1300, width: "100%", maxHeight: 200, overflow: "auto", mt: 0.5, borderRadius: 1 }}>
                    {filtered.map(item => (
                        <ListItemButton key={item.id} onMouseDown={e => e.preventDefault()} onClick={() => { onSelect(item); setFocused(false); }} sx={{ py: 0.5, px: 1.5 }}>
                            <ListItemText primary={item.label} primaryTypographyProps={{ variant: "body2" }} />
                        </ListItemButton>
                    ))}
                </Paper>
            )}
        </Box>
    );
};

const SaleRegister = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [sameWhatsapp, setSameWhatsapp] = useState(false);
    const [geoLocating, setGeoLocating] = useState(false);
    // 'idle' | 'submitting' | 'done'
    const [farmerPhase, setFarmerPhase] = useState("idle");
    const [commitPhase, setCommitPhase] = useState("idle");
    const [registeredFarmerId, setRegisteredFarmerId] = useState(null);
    const [openDealerDialog, setOpenDealerDialog] = useState(false);

    const farmerDone = farmerPhase === "done";
    const isNextLoading = farmerPhase === "submitting";
    const isCommitLoading = commitPhase === "submitting";

    const userDetails = useMemo(() => {
        try { return JSON.parse(localStorage.getItem("userDetails")); } catch { return null; }
    }, []);
    const userId = userDetails?.id;

    /* ── Selectors ── */
    const { successData: districts } = useSelector(s => s.getAllDistrictsReducer);
    const { successData: mandals } = useSelector(s => s.getAllMandalsReducer);
    const { successData: villages } = useSelector(s => s.getAllVillagesReducer);
    const { successData: cropsData } = useSelector(s => s.getAllCropsReducer);
    const { successData: varieties } = useSelector(s => s.getAllVarietiesReducer);
    const { successData: seasons } = useSelector(s => s.getAllSeasonsReducer);
    const { successData: dealers } = useSelector(s => s.getAllDealersReducer);
    const { successData: createFarmerSuccess, error: createFarmerError, errorInfo: createFarmerErrorInfo } = useSelector(s => s.createFarmerCommitmentReducer);
    const { successData: createCommitSuccess, error: createCommitError, errorInfo: createCommitErrorInfo } = useSelector(s => s.farmerCommitmentReducer);

    /* ── Derived lists ── */
    const districtList = useMemo(() => districts?.data?.districts || [], [districts]);

    const mandalList = useMemo(() => mandals?.data?.mandals || [], [mandals]);

    const villageList = useMemo(() => villages?.data?.villages || [], [villages]);
    const cropOptions = useMemo(() => cropsData?.data?.crops || [], [cropsData]);
    const varietyOptions = useMemo(() => varieties?.data?.varieties || [], [varieties]);
    const seasonOptions = useMemo(() => seasons?.data?.seasons || seasons?.seasons || [], [seasons]);
    const dealerOptions = useMemo(() => dealers?.data?.dealers || dealers?.dealers || [], [dealers]);

    /* ── Current season (auto-select) ── */
    const currentSeason = useMemo(() => {
        if (!seasonOptions.length) return null;
        const now = new Date();
        const active = seasonOptions.find(s => s.is_current === true || s.isActive || s.status === "ACTIVE" || s.status === "active");
        if (active) return active;
        const byDate = seasonOptions.find(s => s.startDate && s.endDate && now >= new Date(s.startDate) && now <= new Date(s.endDate));
        if (byDate) return byDate;
        return [...seasonOptions].sort((a, b) => (b.year || 0) - (a.year || 0))[0] || null;
    }, [seasonOptions]);

    const districtSuggestions = useMemo(() =>
        districtList.map(d => ({ id: d.districtId, label: d.name || "" })).filter(d => d.id && d.label),
        [districtList]
    );

    const allDistricts = useMemo(() => districts?.data?.districts || [], [districts]);

    /* ── Validation schema (full) ── */
    const validationSchema = yup.object({
        farmerName: yup.string().trim().required("Farmer name is required"),
        mobileNumber: yup.string().matches(/^\d{10}$/, "Enter 10 digit mobile number").required("Mobile number is required"),
        whatsappNumber: yup.string().matches(/^\d{10}$/, "Enter 10 digit WhatsApp number").nullable(),
        districtName: yup.string().required("District is required"),
        mandalName: yup.string().required("Mandal is required"),
        villageName: yup.string().required("Village is required"),
        focusCrop: yup.string().required("Crop is required"),
        cropId: yup.string().required("Crop is required"),
        varietyId: yup.string().required("Variety is required"),
          totalAcres: yup.number()
            .transform((val, orig) => (orig === "" ? undefined : Number(orig)))
            .required("Total acres is required")
            .positive("Must be greater than 0"),
        purchaseQty: yup.number()
            .transform((val, orig) => (orig === "" ? undefined : Number(orig)))
            .required("No of kgs purchased is required")
            .positive("Must be greater than 0"),
        expectedAcres: yup.number()
            .transform((val, orig) => (orig === "" ? undefined : Number(orig)))
            .required("Expected acres is required")
            .positive("Must be greater than 0"),
        dealerId: yup.string().required("Dealer is required"),
    });

    const formik = useFormik({
        initialValues: {
            farmerName: "", mobileNumber: "", whatsappNumber: "",
            existingUser: false,
            districtId: "", districtName: "",
            mandalId: "", mandalName: "",
            villageId: "", villageName: "",
              totalAcres:"",
            geoCode: "",
            focusCrop: "",
            cropId: "", varietyId: "",
            purchaseQty: "", expectedAcres: "",
            dealerDistrictId: "", dealerId: "",
            billProof: null,
        },
        validationSchema,
        onSubmit: () => {},
    });

    const { setFieldValue } = formik;

    useEffect(() => {
        if (sameWhatsapp) setFieldValue("whatsappNumber", formik.values.mobileNumber);
    }, [sameWhatsapp, formik.values.mobileNumber, setFieldValue]);

    /* ── Auto-select the Purchase-section Crop to match the crop chosen in Agriculture Profile ── */
    useEffect(() => {
        if (!formik.values.focusCrop || !cropOptions.length) return;
        const match = cropOptions.find(c => c.name === formik.values.focusCrop);
        if (match && String(formik.values.cropId) !== String(match.cropId)) {
            setFieldValue("cropId", String(match.cropId));
        }
    }, [formik.values.focusCrop, formik.values.cropId, cropOptions, setFieldValue]);

    const mandalSuggestions = useMemo(() => {
        if (!formik.values.districtId) return [];
        return mandalList
            .filter(m => String(m.districtId) === String(formik.values.districtId))
            .map(m => ({ id: m.mandalId, label: m.name || "" }))
            .filter(m => m.id && m.label);
    }, [mandalList, formik.values.districtId]);

    const villageSuggestions = useMemo(() => {
        if (!formik.values.mandalId) return [];
        return villageList
            .filter(v => String(v.mandalId) === String(formik.values.mandalId))
            .map(v => ({ id: v.villageId, label: v.name || "" }))
            .filter(v => v.id && v.label);
    }, [villageList, formik.values.mandalId]);

    const filteredVarietyOptions = useMemo(() =>
        varietyOptions.filter(v => String(v.cropId) === String(formik.values.cropId) && (v.status ? v.status === "ACTIVE" : true)),
        [varietyOptions, formik.values.cropId]
    );

    const dealerOptionsFiltered = useMemo(() =>
        formik.values.dealerDistrictId
            ? dealerOptions.filter(d => String(d.districtId) === String(formik.values.dealerDistrictId))
            : dealerOptions,
        [dealerOptions, formik.values.dealerDistrictId]
    );

    /* ── Load data ── */
    useEffect(() => {
        dispatch(getAllDistrictsAction());
        dispatch(getAllMandalsAction());
        dispatch(getAllVillagesAction());
        dispatch(getAllCropsAction());
        dispatch(getAllVarietiesAction());
        dispatch(getAllSeasonsAction());
        dispatch(getAllDealersAction());
    }, [dispatch]);

    /* ── Auto-fill single district ── */
    useEffect(() => {
        if (districtSuggestions.length === 1 && !formik.values.districtId) {
            setFieldValue("districtName", districtSuggestions[0].label);
            setFieldValue("districtId", districtSuggestions[0].id);
        }
    }, [districtSuggestions]);

    /* ── Farmer register success / error ── */
    useEffect(() => {
        if (farmerPhase !== "submitting") return;
        if (createFarmerSuccess?.data) {
            setRegisteredFarmerId(createFarmerSuccess.data.farmerId);
            setFarmerPhase("done");
            dispatch(clearCreateFarmerCommitmentAction());
            toast.success("Farmer registered! Fill purchase details below.");
        }
        if (createFarmerError) {
            toast.error(createFarmerErrorInfo || "Failed to register farmer");
            setFarmerPhase("idle");
            dispatch(clearCreateFarmerCommitmentAction());
        }
    }, [createFarmerSuccess, createFarmerError, farmerPhase, dispatch, createFarmerErrorInfo]);

    /* ── Commitment create success / error ── */
    useEffect(() => {
        if (commitPhase !== "submitting") return;
        const success = createCommitSuccess?.success || createCommitSuccess?.data ||
            createCommitSuccess?.statusCode === 201 || createCommitSuccess?.statusCode === 200;
        if (success) {
            toast.success("Walk-in registration completed successfully!");
            dispatch(clearfarmerCommitmentAction());
            dispatch(getAllFarmerCommitmentsAction());
            navigate("/masterData/farmer");
            setCommitPhase("done");
        }
        if (createCommitError) {
            toast.error(createCommitErrorInfo || "Failed to create commitment");
            setCommitPhase("idle");
            dispatch(clearfarmerCommitmentAction());
        }
    }, [createCommitSuccess, createCommitError, commitPhase, dispatch, navigate, createCommitErrorInfo]);

    /* ── Next: validate farmer fields and hit register API ── */
    const handleNext = async () => {
        const errs = await formik.validateForm();
        formik.setTouched({
            farmerName: true, mobileNumber: true,
            districtName: true, mandalName: true, villageName: true,
            focusCrop: true,totalAcres:true,
        });
        const farmerFieldErrors = ["farmerName", "mobileNumber", "districtName", "mandalName", "villageName", "focusCrop","totalAcres"];
        if (farmerFieldErrors.some(f => errs[f])) return;

        const v = formik.values;
        const farmerPayload = {
            name: v.farmerName,
            mobile: v.mobileNumber,
            whatsappNo: v.whatsappNumber,
            districtId: v.districtId || v.districtName.trim(),
            mandalId: v.mandalId || v.mandalName.trim(),
            villageId: v.villageId || v.villageName.trim(),
            incharge_user_id: userId,
            geoCode: v.geoCode,
            majorCrops: v.focusCrop ? [v.focusCrop] : [],
            focusCrop: v.focusCrop,
            irrigationType: "rainfed",
             totalAcres: Number(v.totalAcres),
            acresUnderIrrigation: "",
            existingUser: v.existingUser,
        };

        setFarmerPhase("submitting");
        dispatch(createFarmerCommitmentAction(farmerPayload));
    };

    /* ── Commit: validate purchase fields and hit commitment API ── */
    const handleCommit = async () => {
        const errs = await formik.validateForm();
        formik.setTouched({ cropId: true, varietyId: true, purchaseQty: true, expectedAcres: true, dealerId: true });
        const purchaseFieldErrors = ["cropId", "varietyId", "purchaseQty", "expectedAcres", "dealerId"];
        if (purchaseFieldErrors.some(f => errs[f])) return;

        const seasonId = currentSeason?.seasonId;
        if (!seasonId) {
            toast.error("No active season found.");
            return;
        }

        const v = formik.values;
        const selectedVariety = filteredVarietyOptions.find(vv => String(vv.varietyId) === String(v.varietyId));
        const fd = new FormData();
        fd.append("farmer_id", Number(registeredFarmerId));
        fd.append("season_id", Number(seasonId));
        fd.append("crop_id", Number(v.cropId));
        fd.append("variety_id", Number(v.varietyId));
        fd.append("variety_name", selectedVariety?.name || "");
        fd.append("expected_qty", v.purchaseQty ? String(v.purchaseQty) : "0");
        fd.append("expected_acre", v.expectedAcres ? String(v.expectedAcres) : "0");
        fd.append("expected_dealer_id[0]", Number(v.dealerId));
        fd.append("purchased_dealer_id", Number(v.dealerId));
        fd.append("purchase_qty", v.purchaseQty ? String(v.purchaseQty) : "0");
        fd.append("status", "Approved");
        if (v.billProof) fd.append("inv_photo", v.billProof);

        setCommitPhase("submitting");
        dispatch(farmerCommitmentAction(fd));
    };

    const sectionHeader = (icon, label) => (
        <Box display="flex" alignItems="center" gap={1.5} mb={2} mt={4}>
            {icon}
            <Typography fontWeight={600}>{label}</Typography>
        </Box>
    );

    return (
        <Paper elevation={0} sx={{ mx: "auto", p: { xs: 2, sm: 3, md: 4 }, mt: { xs: 7, sm: 0 }, borderRadius: 3, position: "relative" }}>
            <IconButton
                aria-label="Close"
                onClick={() => navigate(-1)}
                sx={{ position: "absolute", top: { xs: 2, sm: 10, md: 4 }, right: { xs: 2, sm: 10, md: 4 }, bgcolor: "#F3F4F6", "&:hover": { bgcolor: "#E5E7EB" } }}
                size="small"
            >
                <CloseIcon fontSize="small" />
            </IconButton>

            {/* Header */}
            <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2} justifyContent="space-between" alignItems={{ sm: "center" }} mt={{ xs: 0, sm: 4, md: 0 }}>
                <Box display="flex" gap={1.5}>
                    <Avatar sx={{ bgcolor: "black", width: 32, height: 32 }}>
                        <CheckCircleIcon fontSize="small" />
                    </Avatar>
                    <Box>
                        <Typography fontWeight={600}>Post Sale Registration</Typography>
                        <Typography fontSize={14} color="text.secondary">
                            Register an unregistered farmer and record their purchase in one step.
                        </Typography>
                    </Box>
                </Box>
                <Chip
                    label={currentSeason ? `Season: ${currentSeason.name} (${currentSeason.year})` : "No Active Season"}
                    sx={{ bgcolor: currentSeason ? "#D1FAE5" : "#FEE2E2", color: currentSeason ? "#065F46" : "#B91C1C", width: "fit-content" }}
                />
            </Box>

            {/* ── Personal Information ── */}
            {sectionHeader(<PersonIcon fontSize="small" />, "Personal Information")}
            <Grid container spacing={3}>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>Farmer Name <span style={{ color: "red" }}>*</span></Typography>
                    <TextField
                        size="small" fullWidth name="farmerName"
                        value={formik.values.farmerName} onChange={formik.handleChange} onBlur={formik.handleBlur}
                        placeholder="Enter farmer name" disabled={farmerDone}
                        error={formik.touched.farmerName && Boolean(formik.errors.farmerName)}
                        helperText={formik.touched.farmerName && formik.errors.farmerName}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: farmerDone ? "#F3F4F6" : "#FAFAFA" } }}
                    />
                </Grid>

                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>Mobile Number <span style={{ color: "red" }}>*</span></Typography>
                    <TextField
                        size="small" fullWidth name="mobileNumber" type="text"
                        value={formik.values.mobileNumber}
                        onChange={e => {
                            const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
                            setFieldValue("mobileNumber", val);
                            if (sameWhatsapp) setFieldValue("whatsappNumber", val);
                        }}
                        onBlur={formik.handleBlur}
                        inputProps={{ maxLength: 10, inputMode: "numeric" }}
                        placeholder="Enter mobile number" disabled={farmerDone}
                        error={formik.touched.mobileNumber && Boolean(formik.errors.mobileNumber)}
                        helperText={formik.touched.mobileNumber && formik.errors.mobileNumber}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: farmerDone ? "#F3F4F6" : "#FAFAFA" } }}
                    />
                </Grid>

                <Grid item size={{ xs: 12, sm: 6, md: 1 }} display="flex" alignItems="center" justifyContent="center" ml={{ xs: -10, sm: -10, md: 2 }} mr={4}>
                    <FormControlLabel
                        control={<Checkbox checked={sameWhatsapp} onChange={e => setSameWhatsapp(e.target.checked)} color="primary" disabled={farmerDone} />}
                        label={<Typography fontSize={12}>same as mobile</Typography>}
                        sx={{ whiteSpace: "nowrap", m: 0 }}
                    />
                </Grid>

                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>WhatsApp Number</Typography>
                    <TextField
                        size="small" fullWidth name="whatsappNumber" type="text"
                        value={formik.values.whatsappNumber}
                        onChange={e => { if (!sameWhatsapp) setFieldValue("whatsappNumber", e.target.value.replace(/[^0-9]/g, "").slice(0, 10)); }}
                        inputProps={{ maxLength: 10, inputMode: "numeric" }}
                        onBlur={formik.handleBlur}
                        placeholder="Enter WhatsApp number"
                        disabled={farmerDone || sameWhatsapp}
                        error={formik.touched.whatsappNumber && Boolean(formik.errors.whatsappNumber)}
                        helperText={formik.touched.whatsappNumber && formik.errors.whatsappNumber}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: (farmerDone || sameWhatsapp) ? "#F3F4F6" : "#FAFAFA" } }}
                    />
                </Grid>

                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>User / Non User</Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={Boolean(formik.values.existingUser)}
                                onChange={e => setFieldValue("existingUser", e.target.checked)}
                                name="existingUser" color="primary" disabled={farmerDone}
                            />
                        }
                        label={formik.values.existingUser ? "Existing User" : "Non User"}
                    />
                </Grid>
            </Grid>

            {/* ── Location Details ── */}
            {sectionHeader(<LocationOnIcon fontSize="small" />, "Location Details")}
            <Grid container spacing={3}>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <LocationSearchInput
                        label="District *" value={formik.values.districtName} selectedId={formik.values.districtId}
                        suggestions={districtSuggestions} disabled={farmerDone || districtSuggestions.length === 1}
                        error={formik.touched.districtName && Boolean(formik.errors.districtName)}
                        helperText={formik.touched.districtName && formik.errors.districtName}
                        onTextChange={text => {
                            setFieldValue("districtName", text);
                            formik.setFieldTouched("districtName", true, false);
                            const norm = s => (s || "").toLowerCase().replace(/\s+/g, "");
                            const match = districtSuggestions.find(d => norm(d.label) === norm(text));
                            setFieldValue("districtId", match ? match.id : "");
                            setFieldValue("mandalName", ""); setFieldValue("mandalId", "");
                            setFieldValue("villageName", ""); setFieldValue("villageId", "");
                        }}
                        onSelect={item => {
                            setFieldValue("districtName", item.label); setFieldValue("districtId", item.id);
                            formik.setFieldTouched("districtName", true, false);
                            setFieldValue("mandalName", ""); setFieldValue("mandalId", "");
                            setFieldValue("villageName", ""); setFieldValue("villageId", "");
                        }}
                    />
                </Grid>

                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <LocationSearchInput
                        label="Mandal *" value={formik.values.mandalName} selectedId={formik.values.mandalId}
                        suggestions={mandalSuggestions} disabled={farmerDone || !formik.values.districtName?.trim()}
                        error={formik.touched.mandalName && Boolean(formik.errors.mandalName)}
                        helperText={formik.touched.mandalName && formik.errors.mandalName}
                        onTextChange={text => {
                            setFieldValue("mandalName", text);
                            formik.setFieldTouched("mandalName", true, false);
                            const norm = s => (s || "").toLowerCase().replace(/\s+/g, "");
                            const match = mandalSuggestions.find(m => norm(m.label) === norm(text));
                            setFieldValue("mandalId", match ? match.id : "");
                            setFieldValue("villageName", ""); setFieldValue("villageId", "");
                        }}
                        onSelect={item => {
                            setFieldValue("mandalName", item.label); setFieldValue("mandalId", item.id);
                            formik.setFieldTouched("mandalName", true, false);
                            setFieldValue("villageName", ""); setFieldValue("villageId", "");
                        }}
                    />
                </Grid>

                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <LocationSearchInput
                        label="Village *" value={formik.values.villageName} selectedId={formik.values.villageId}
                        suggestions={villageSuggestions} disabled={farmerDone || !formik.values.mandalName?.trim()}
                        error={formik.touched.villageName && Boolean(formik.errors.villageName)}
                        helperText={formik.touched.villageName && formik.errors.villageName}
                        onTextChange={text => {
                            setFieldValue("villageName", text);
                            formik.setFieldTouched("villageName", true, false);
                            const norm = s => (s || "").toLowerCase().replace(/\s+/g, "");
                            const match = villageSuggestions.find(v => norm(v.label) === norm(text));
                            setFieldValue("villageId", match ? match.id : "");
                        }}
                        onSelect={item => {
                            setFieldValue("villageName", item.label); setFieldValue("villageId", item.id);
                            formik.setFieldTouched("villageName", true, false);
                        }}
                    />
                </Grid>

                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ flex: 1, px: 1, py: 0.9, borderRadius: 2, border: "1px solid #E0E0E0", minHeight: 25, display: "flex", alignItems: "center", bgcolor: farmerDone ? "#F3F4F6" : "transparent" }}>
                            <Typography fontSize={13} color={formik.values.geoCode ? "text.primary" : "text.disabled"} noWrap>
                                {formik.values.geoCode || "Auto-filled on get location"}
                            </Typography>
                        </Box>
                        {!farmerDone && (
                            <Tooltip title="Get current location">
                                <span>
                                    <IconButton size="small" color="primary" disabled={geoLocating} onClick={() => {
                                        if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
                                        setGeoLocating(true);
                                        navigator.geolocation.getCurrentPosition(
                                            pos => { setFieldValue("geoCode", `${pos.coords.latitude.toFixed(6)},${pos.coords.longitude.toFixed(6)}`); setGeoLocating(false); },
                                            err => { toast.error("Unable to retrieve location: " + err.message); setGeoLocating(false); },
                                            { enableHighAccuracy: true, timeout: 10000 }
                                        );
                                    }}>
                                        {geoLocating ? <CircularProgress size={18} /> : <MyLocationIcon fontSize="small" />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                </Grid>
            </Grid>

            {/* ── Agriculture Profile ── */}
            {sectionHeader(<AgricultureIcon fontSize="small" />, "Agriculture Profile")}
            <Grid container spacing={3}>
                  <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>Acres <span style={{ color: "red" }}>*</span></Typography>
                        <TextField
                            size="small" fullWidth name="totalAcres" type="number"
                            value={formik.values.totalAcres} onChange={formik.handleChange} onBlur={formik.handleBlur}
                            placeholder="Enter  acres"
                            error={formik.touched.totalAcres && Boolean(formik.errors.totalAcres)}
                            helperText={formik.touched.totalAcres && formik.errors.totalAcres}
                            inputProps={{ onWheel: e => e.target.blur() }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#FAFAFA" }, "& input[type=number]": { MozAppearance: "textfield" }, "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none" } }}
                        />
                    </Grid>
                    
                <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>Crop <span style={{ color: "red" }}>*</span></Typography>
                    <Autocomplete
                        options={cropOptions.map(c => c.name) || []}
                        value={formik.values.focusCrop || null}
                        onChange={(_, val) => {
                            setFieldValue("focusCrop", val || "");
                            formik.setFieldTouched("focusCrop", true, false);
                        }}
                        getOptionLabel={o => o || ""}
                        disabled={farmerDone}
                        renderInput={params => (
                            <TextField {...params} size="small" placeholder="Search and select crop"
                                error={formik.touched.focusCrop && Boolean(formik.errors.focusCrop)}
                                helperText={formik.touched.focusCrop && formik.errors.focusCrop}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: farmerDone ? "#F3F4F6" : "#FAFAFA" } }} />
                        )}
                    />
                </Grid>
            </Grid>

            {/* ── Next button row ── */}
            <Box display="flex" justifyContent="flex-end" mt={3} mb={1}>
                {farmerDone ? (
                    <Chip label="Farmer Registered" sx={{ bgcolor: "#D1FAE5", color: "#065F46", fontWeight: 600, px: 1 }} icon={<CheckCircleIcon sx={{ color: "#065F46 !important", fontSize: 16 }} />} />
                ) : (
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={isNextLoading}
                        startIcon={isNextLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ background: "#4F46E5", borderRadius: 2, fontWeight: 600, textTransform: "none", px: 4, py: 1.2, boxShadow: "0px 4px 16px #5B5FEF1A" }}
                    >
                        {isNextLoading ? "Registering..." : "Next"}
                    </Button>
                )}
            </Box>

            {/* ── Purchase Information ── */}
            <Box sx={{ opacity: farmerDone ? 1 : 0.45, pointerEvents: farmerDone ? "auto" : "none", transition: "opacity 0.3s" }}>
                {sectionHeader(<AddShoppingCartIcon fontSize="small" />, "Purchase Information")}
                <Grid container spacing={3}>
                    <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>Crop <span style={{ color: "red" }}>*</span></Typography>
                        <Autocomplete
                            options={cropOptions.filter(c => c.status ? c.status === "ACTIVE" : true)}
                            value={cropOptions.find(c => String(c.cropId) === String(formik.values.cropId)) || null}
                            getOptionLabel={c => c ? c.name : ""}
                            isOptionEqualToValue={(o, v) => String(o.cropId) === String(v.cropId)}
                            disabled
                            renderInput={params => (
                                <TextField {...params} size="small" placeholder="Auto-selected from Agriculture Profile"
                                    error={formik.touched.cropId && Boolean(formik.errors.cropId)}
                                    helperText={formik.touched.cropId && formik.errors.cropId}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#F3F4F6" } }} />
                            )}
                        />
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>Variety <span style={{ color: "red" }}>*</span></Typography>
                        <Autocomplete
                            options={filteredVarietyOptions}
                            value={filteredVarietyOptions.find(v => String(v.varietyId) === String(formik.values.varietyId)) || null}
                            onChange={(_, val) => {
                                setFieldValue("varietyId", val ? String(val.varietyId) : "");
                                formik.setFieldTouched("varietyId", true, false);
                            }}
                            getOptionLabel={v => v ? v.name : ""}
                            isOptionEqualToValue={(o, v) => String(o.varietyId) === String(v.varietyId)}
                            disabled={!formik.values.cropId}
                            renderInput={params => (
                                <TextField {...params} size="small"
                                    placeholder={formik.values.cropId ? "Select variety type" : "Select crop first"}
                                    error={formik.touched.varietyId && Boolean(formik.errors.varietyId)}
                                    helperText={formik.touched.varietyId && formik.errors.varietyId}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#FAFAFA" } }} />
                            )}
                        />
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>No of Kgs Purchased <span style={{ color: "red" }}>*</span></Typography>
                        <TextField
                            size="small" fullWidth name="purchaseQty" type="number"
                            value={formik.values.purchaseQty} onChange={formik.handleChange} onBlur={formik.handleBlur}
                            placeholder="Enter quantity in kgs"
                            error={formik.touched.purchaseQty && Boolean(formik.errors.purchaseQty)}
                            helperText={formik.touched.purchaseQty && formik.errors.purchaseQty}
                            inputProps={{ onWheel: e => e.target.blur() }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#FAFAFA" }, "& input[type=number]": { MozAppearance: "textfield" }, "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none" } }}
                        />
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>Expected Acres <span style={{ color: "red" }}>*</span></Typography>
                        <TextField
                            size="small" fullWidth name="expectedAcres" type="number"
                            value={formik.values.expectedAcres} onChange={formik.handleChange} onBlur={formik.handleBlur}
                            placeholder="Enter expected acres"
                            error={formik.touched.expectedAcres && Boolean(formik.errors.expectedAcres)}
                            helperText={formik.touched.expectedAcres && formik.errors.expectedAcres}
                            inputProps={{ onWheel: e => e.target.blur() }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#FAFAFA" }, "& input[type=number]": { MozAppearance: "textfield" }, "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none" } }}
                        />
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>Place of Dealer (District)</Typography>
                        <Autocomplete
                            options={allDistricts}
                            value={allDistricts.find(d => String(d.districtId) === String(formik.values.dealerDistrictId)) || null}
                            onChange={(_, val) => {
                                setFieldValue("dealerDistrictId", val ? String(val.districtId) : "");
                                setFieldValue("dealerId", "");
                            }}
                            getOptionLabel={d => d ? d.name : ""}
                            isOptionEqualToValue={(o, v) => String(o.districtId) === String(v.districtId)}
                            renderInput={params => (
                                <TextField {...params} size="small" placeholder="Select dealer district"
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#FAFAFA" } }} />
                            )}
                        />
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>Name of Dealer <span style={{ color: "red" }}>*</span></Typography>
                        <Autocomplete
                            fullWidth
                            options={dealerOptionsFiltered}
                            value={dealerOptionsFiltered.find(d => String(d.dealerId) === String(formik.values.dealerId)) || null}
                            onChange={(_, val) => {
                                setFieldValue("dealerId", val ? String(val.dealerId) : "");
                                formik.setFieldTouched("dealerId", true, false);
                            }}
                            getOptionLabel={d => d ? d.name : ""}
                            isOptionEqualToValue={(o, v) => String(o.dealerId) === String(v.dealerId)}
                            renderInput={params => (
                                <TextField {...params} size="small" placeholder="Select dealer"
                                    error={formik.touched.dealerId && Boolean(formik.errors.dealerId)}
                                    helperText={formik.touched.dealerId && formik.errors.dealerId}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#FAFAFA" } }} />
                            )}
                        />
                        <Box display="flex" justifyContent="flex-end" mt={0.75}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon fontSize="small" />}
                                onClick={() => setOpenDealerDialog(true)}
                                sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderRadius: 2, whiteSpace: "nowrap" }}
                            >
                                Add New Dealer
                            </Button>
                        </Box>
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>Bill Proof</Typography>
                        <Box sx={{ border: "1px solid #E0E0E0", borderRadius: 2, p: 1.5, backgroundColor: "#FAFAFA", display: "flex", alignItems: "center", gap: 2 }}>
                            <Button variant="outlined" component="label" size="small" sx={{ borderRadius: 2, textTransform: "none", whiteSpace: "nowrap" }}>
                                Upload Bill
                                <input type="file" accept="image/*" hidden onChange={e => setFieldValue("billProof", e.target.files[0] || null)} />
                            </Button>
                            <Typography fontSize={13} color={formik.values.billProof ? "text.primary" : "text.disabled"} noWrap>
                                {formik.values.billProof ? formik.values.billProof.name : "No file chosen"}
                            </Typography>
                            {formik.values.billProof && (
                                <IconButton size="small" onClick={() => setFieldValue("billProof", null)}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                    </Grid>
                </Grid>

                {/* ── Actions ── */}
                <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
                    <Button variant="outlined" sx={{ borderRadius: 2 }} onClick={() => navigate("/masterData/farmer")} disabled={isCommitLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={isCommitLoading || !farmerDone}
                        onClick={handleCommit}
                        startIcon={isCommitLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ background: "#4F46E5", borderRadius: 2, fontWeight: 600, textTransform: "none", px: 3, py: 1.2, boxShadow: "0px 4px 16px #5B5FEF1A" }}
                    >
                        {isCommitLoading ? "Processing..." : "Sales Register & Commit"}
                    </Button>
                </Box>
            </Box>

            <CreateDealerDialog
                open={openDealerDialog}
                onClose={() => setOpenDealerDialog(false)}
                onCreated={(newDealerId) => {
                    setFieldValue("dealerId", String(newDealerId));
                    formik.setFieldTouched("dealerId", true, false);
                }}
            />
        </Paper>
    );
};

export default SaleRegister;
